import { computed, ref } from '@nuxtjs/composition-api'
import { ConditionOptionCommon } from '~/types/services/search'
import { Category } from '~/stub/apigateway/master/master_pb'
import { SERVICE_KIND_VALUE } from '~/constants/search'

const SEARCH_CONDITION_ITEMS_LIMIT = 6
const EXPAND_TRIGGER_LABEL = Object.freeze({
  OMITTED: 'もっと見る',
  EXPANDED: '閉じる'
})

export function useSearchConditionSelect(
  searchCondition: ConditionOptionCommon,
  selectedCondition,
  categoryMaster: Category.AsObject
) {
  // 選択されている文字単価（Min）
  const selectedUnitPriceMin = computed(() => {
    return selectedCondition.unit_price_min ? String(selectedCondition.unit_price_min) : ''
  })
  // 選択されている文字単価（Max）
  const selectedUnitPriceMax = computed(() => {
    return selectedCondition.unit_price_max ? String(selectedCondition.unit_price_max) : ''
  })

  // 初期表示で表示する検索条件
  const omittedSearchConditionItems = computed(() => {
    return searchCondition.master.valuesList.slice(0, SEARCH_CONDITION_ITEMS_LIMIT)
  })

  // 「もっと見る」をクリックした時に表示される残りの検索条件
  const expandSearchConditionItems = computed(() => {
    if (searchCondition.master.valuesList.length > SEARCH_CONDITION_ITEMS_LIMIT) {
      return searchCondition.master.valuesList.slice(
        SEARCH_CONDITION_ITEMS_LIMIT,
        searchCondition.master.valuesList.length
      )
    }
    return []
  })

  const isExpanded = ref<boolean>(false)

  // もっと見るリンクのラベル
  const expandTriggerLabel = computed(() => {
    if (isExpanded.value) {
      return EXPAND_TRIGGER_LABEL.EXPANDED
    } else {
      return `${EXPAND_TRIGGER_LABEL.OMITTED}(${expandSearchConditionItems.value.length})`
    }
  })

  /**
   * もっと見るをクリックした時の表示切り替えを行う
   */
  const onClickExpandTrigger = (): void => {
    isExpanded.value = !isExpanded.value
  }

  /**
   * 絞り込み項目を表示するかの判定
   */
  const isShowSelectCassette = computed(() => {
    const searchConditionKey = searchCondition.master.key

    // 出品者状況と追加オプション以外の絞り込み項目は無条件で表示する
    if (searchConditionKey !== 'provider_status' && searchConditionKey !== 'additional_options') {
      return true
    }

    const serviceKindValue = selectedCondition['service_kind']
    // 出品者状況は「サービスの種類」で電話が選択された時だけ絞り込み項目を表示する
    if (searchConditionKey === 'provider_status' && serviceKindValue === SERVICE_KIND_VALUE.PHONE) {
      return true
    }

    // 追加オプションの項目は下記の条件時に表示する
    // ・ 初期表示時に何も「サービスの種類」が選択されていない場合
    // ・ 「サービスの種類」でテキストかビデオチャットが選択された場合
    if (
      searchConditionKey === 'additional_options' &&
      ['', SERVICE_KIND_VALUE.TEXT, SERVICE_KIND_VALUE.VIDEO].includes(serviceKindValue)
    ) {
      return true
    }
    return false
  })

  /**
   * 価格の変更を反映する
   * @param $event
   */
  const changePrice = $event => {
    selectedCondition[$event.key] = $event.value
  }

  /**
   * 表示していい絞り込み条件かを判定する
   */
  const isShowSearchConditionItem = (key: string, value: string): boolean => {
    const selectedServiceKindCondition = selectedCondition['service_kind']
    // ビデオチャットサービスが選択されている状態
    if (selectedServiceKindCondition === SERVICE_KIND_VALUE.VIDEO) {
      // ビデオチャットサービスで絞り込まれている状態でビデオチャットサービスを選択した時には「ビデオチャット使用」は表示しない
      if (key === 'provider_statuses' && value === 'video_service') {
        return false
      }
    }

    // 全てのカテゴリの時はカテゴリマスターの情報が無いので全て表示する
    if (!categoryMaster) {
      return true
    }
    // 「物品配送可能」かどうかはをカテゴリマスターを見て判定する
    if (key === 'shipment_flag') {
      return categoryMaster.isShipmentServiceCategory
    }

    // サービスの種類のビデオサービスの選択肢はカテゴリマスターを見て判定する
    if (key === 'service_kind' && value === SERVICE_KIND_VALUE.VIDEO) {
      return categoryMaster.isVideoServiceCategory
    }

    // 「ビデオチャット使用」可能かどうかカテゴリマスターを見て判定する
    if (key === 'provider_statuses' && value === 'video_service') {
      return categoryMaster.isVideoServiceCategory
    }
    return true
  }

  return {
    selectedUnitPriceMin,
    selectedUnitPriceMax,
    omittedSearchConditionItems,
    expandSearchConditionItems,
    isExpanded,
    expandTriggerLabel,
    onClickExpandTrigger,
    isShowSelectCassette,
    isShowSearchConditionItem,
    changePrice
  }
}
